import { Chip, Collapse, Icon, IconButton, LinearProgress, Paper, Typography } from '@shared/ui';
import { type KeyboardEventHandler, useState } from 'react';
import { registerWidgetRenderer } from '../registry';
import type { TeamAnalysisEntity, TimelineWidgetProps } from '../types';

export interface TeamAnalysisWidgetProps extends TimelineWidgetProps<TeamAnalysisEntity> {
  debugMode?: boolean;
}

export function TeamAnalysisWidget({ entity, debugMode = false }: TeamAnalysisWidgetProps) {
  const {
    status,
    teamSize,
    depth,
    progress,
    currentPair,
    networkScore,
    insights,
    visualizationData,
  } = entity.props;
  const [expanded, setExpanded] = useState(true);
  const [showViz, setShowViz] = useState(false);

  const toggleExpanded = () => setExpanded((prev) => !prev);
  const handleExpandedKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleExpanded();
    }
  };

  const toggleVisualization = () => setShowViz((prev) => !prev);
  const handleVisualizationKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleVisualization();
    }
  };

  const statusColor = status === 'completed' ? 'success' : status === 'error' ? 'error' : 'primary';
  const progressPercent = (progress || 0) * 100;

  return (
    <Paper
      variant="outlined"
      className="p-4 w-full min-w-0 bg-gradient-to-r from-blue-50 to-purple-50"
    >
      <div className="flex items-start gap-3 min-w-0">
        <Icon sx={{ fontSize: 24, color: `${statusColor}.main`, flexShrink: 0 }}>
          {status === 'analyzing'
            ? 'people_outline'
            : status === 'completed'
              ? 'group'
              : 'error_outline'}
        </Icon>

        <div className="flex-1 min-w-0">
          <div
            className="flex items-center gap-2 cursor-pointer min-w-0"
            role="button"
            tabIndex={0}
            onClick={toggleExpanded}
            onKeyDown={handleExpandedKeyDown}
            aria-expanded={expanded}
            aria-label="Toggle team analysis details"
          >
            <Typography variant="subtitle1" fontWeight="bold" className="min-w-0 flex-shrink-0">
              🔍 Team Relationship Analysis
            </Typography>
            {teamSize ? (
              <Chip
                label={`${teamSize} members`}
                size="small"
                color="primary"
                variant="outlined"
                className="flex-shrink-0"
              />
            ) : null}
            {depth ? (
              <Chip label={depth} size="small" variant="outlined" className="flex-shrink-0" />
            ) : null}
            <IconButton size="small" sx={{ ml: 'auto', flexShrink: 0 }}>
              <Icon sx={{ fontSize: 18 }}>{expanded ? 'expand_less' : 'expand_more'}</Icon>
            </IconButton>
          </div>

          <Collapse in={expanded}>
            {status === 'analyzing' ? (
              <div className="mt-3">
                <div className="flex justify-between items-center mb-1">
                  <Typography variant="caption" color="text.secondary">
                    {currentPair || 'Analyzing relationships...'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {progressPercent.toFixed(0)}%
                  </Typography>
                </div>
                <LinearProgress variant="determinate" value={progressPercent} />
              </div>
            ) : null}

            {status === 'completed' ? (
              <div className="mt-3 space-y-3">
                {networkScore !== undefined ? (
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <Typography variant="subtitle2" className="mb-1">
                      Network Health Score
                    </Typography>
                    <div className="flex items-center gap-2">
                      <LinearProgress
                        variant="determinate"
                        value={(networkScore || 0) * 100}
                        sx={{ flex: 1, height: 8, borderRadius: 4 }}
                      />
                      <Typography variant="h6" color="primary">
                        {((networkScore || 0) * 100).toFixed(0)}%
                      </Typography>
                    </div>
                  </div>
                ) : null}

                {insights && insights.length > 0 ? (
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <Typography variant="subtitle2" className="mb-2">
                      💡 Key Insights
                    </Typography>
                    <ul className="space-y-1">
                      {insights.map((insight, idx) => (
                        <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                          <span className="text-blue-500 mt-1">•</span>
                          <span>{insight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {visualizationData ? (
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <div
                      className="flex items-center gap-2 cursor-pointer"
                      role="button"
                      tabIndex={0}
                      onClick={toggleVisualization}
                      onKeyDown={handleVisualizationKeyDown}
                      aria-expanded={showViz}
                      aria-label="Toggle network visualization"
                    >
                      <Typography variant="subtitle2">📊 Network Visualization</Typography>
                      <IconButton size="small" sx={{ ml: 'auto' }}>
                        <Icon sx={{ fontSize: 18 }}>
                          {showViz ? 'visibility_off' : 'visibility'}
                        </Icon>
                      </IconButton>
                    </div>
                    <Collapse in={showViz}>
                      <div className="mt-3">
                        <NetworkDiagram data={visualizationData} />
                      </div>
                    </Collapse>
                  </div>
                ) : null}

                {debugMode ? (
                  <details className="bg-gray-100 rounded p-2 min-w-0">
                    <summary className="cursor-pointer text-xs font-mono">Debug Info</summary>
                    <div className="overflow-x-auto max-w-full mt-2">
                      <pre className="text-xs overflow-x-auto max-h-48 whitespace-pre">
                        {JSON.stringify(entity.props, null, 2)}
                      </pre>
                    </div>
                  </details>
                ) : null}
              </div>
            ) : null}
          </Collapse>
        </div>
      </div>
    </Paper>
  );
}

function NetworkDiagram({ data }: { data: any }) {
  const { nodes = [], edges = [] } = data || {};
  return (
    <div className="border rounded p-4 bg-gray-50">
      <Typography variant="caption" color="text.secondary" className="block mb-2">
        Network Graph ({nodes.length} nodes, {edges.length} connections)
      </Typography>
      <div className="space-y-1 max-h-48 overflow-auto">
        {edges.slice(0, 10).map((edge: any, idx: number) => (
          <div key={idx} className="text-xs flex items-center gap-2">
            <Chip label={edge.source} size="small" />
            <span className="text-gray-400">←→</span>
            <Chip label={edge.target} size="small" />
            <span
              className={`px-2 py-0.5 rounded ${
                edge.sentiment === 'positive'
                  ? 'bg-green-100 text-green-700'
                  : edge.sentiment === 'negative'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-gray-100 text-gray-700'
              }`}
            >
              {edge.sentiment}
            </span>
            <span className="text-gray-500">{((edge.strength || 0) * 100).toFixed(0)}%</span>
          </div>
        ))}
        {edges.length > 10 ? (
          <Typography variant="caption" color="text.secondary">
            ... and {edges.length - 10} more connections
          </Typography>
        ) : null}
      </div>
    </div>
  );
}

// Register renderer + visibility
registerWidgetRenderer(
  'team_analysis',
  ({ entity, debugMode }) => (
    <TeamAnalysisWidget key={(entity as any).id} entity={entity as any} debugMode={debugMode} />
  ),
  { visibility: { normal: true, debug: true } }
);
